import themes from "daisyui/src/theming/themes";
import { ConfigProps } from "./types/config";

// TODO move these into config
export const NUMBER_OF_RECENTLY_SENT_QUOTES_TO_PERSIST = 31;
export const LOCAL_SMS_ENABLED: boolean = true;
export const LOCAL_EMAIL_ENABLED: boolean = true;
export const MAX_NUMBER_COLLECTIONS_PER_SUBSCRIPTION = 5;
export const MAX_NUMBER_OF_CONTACTS_PER_USER = 10;

const pricingConfig = {
  basic: {
    devPriceId: "price_1PSCOlLR2OO15vy3pZ4E4NLe",
    prodPriceId: "price_1PaA7dLR2OO15vy3Kv2zeyNf",
    devAnnualPriceId: "price_1Qw7WgLR2OO15vy3L8XKX3OC",
    prodAnnualPriceId: "price_1Qw7VvLR2OO15vy30qQUEJdP",
    maxQuoteSubscriptions: 1,
  },
  premium: {
    devPriceId: "price_1PSCPCLR2OO15vy3BA9RA1Bp",
    prodPriceId: "price_1PaA6CLR2OO15vy3eMx9RdPM",
    devAnnualPriceId: "price_1Qw7XFLR2OO15vy3lkh1w97n",
    prodAnnualPriceId: "price_1Qw7XaLR2OO15vy3018mcMHJ",
    maxQuoteSubscriptions: 5,
  },
};
export const MAX_QUOTE_SUBSCRIPTIONS_ACROSS_PLANS = Math.max(
  ...Object.values(pricingConfig).map((plan) => plan.maxQuoteSubscriptions)
);

export const isUserOnMaxPlan = (plan: string): boolean => {
  return plan === "premium";
};

export const getMaxQuoteSubscriptions = (plan: string): number => {
  if (plan in pricingConfig === false) {
    return 0;
  }
  return pricingConfig[plan as keyof typeof pricingConfig]
    .maxQuoteSubscriptions;
};

const getPriceId = (plan: keyof typeof pricingConfig) => {
  return process.env.NODE_ENV === "development"
    ? pricingConfig[plan].devPriceId
    : pricingConfig[plan].prodPriceId;
};

const getAnnualPriceId = (plan: keyof typeof pricingConfig) => {
  return process.env.NODE_ENV === "development"
    ? pricingConfig[plan].devAnnualPriceId
    : pricingConfig[plan].prodAnnualPriceId;
};

const urlForStageRecord: Record<"development" | "production" | "test", string> =
  {
    development: "dev.quotepulse.club",
    test: "dev.quotepulse.club",
    production: "quotepulse.club",
  };
const URL_BASE = urlForStageRecord[process.env.NODE_ENV];

export const getPlanNameFromPriceId = (
  priceId: string
): keyof typeof pricingConfig | undefined => {
  const monthlyPlan = Object.keys(pricingConfig).find(
    (plan) => getPriceId(plan as keyof typeof pricingConfig) === priceId
  ) as keyof typeof pricingConfig;
  const annualPlan = Object.keys(pricingConfig).find(
    (plan) => getAnnualPriceId(plan as keyof typeof pricingConfig) === priceId
  ) as keyof typeof pricingConfig;
  return monthlyPlan || annualPlan;
};

const config = {
  // REQUIRED
  appName: "QuotePulse",
  // REQUIRED: a short description of your app for SEO tags (can be overwritten)
  appDescription:
    "Recieve motivational quotes to your phone or email every day.",
  // REQUIRED (no https://, not trialing slash at the end, just the naked domain)
  adminEmails: ["n3s.online@gmail.com"],
  domainName: "quotepulse.club",
  crisp: {
    // Crisp website ID. IF YOU DON'T USE CRISP: just remove this => Then add a support email in this config file (mailgun.supportEmail) otherwise customer support won't work.
    // id: "",
    // Hide Crisp by default, except on route "/". Crisp is toggled with <ButtonSupport/>. If you want to show Crisp on every routes, just remove this below
    onlyShowOnRoutes: ["/"],
  },
  stripe: {
    // Create multiple plans in your Stripe dashboard, then add them here. You can add as many plans as you want, just make sure to add the priceId
    plans: [
      {
        // REQUIRED — we use this to find the plan in the webhook (for instance if you want to update the user's credits based on the plan)
        priceId: getPriceId("basic"),
        annualPriceId: getAnnualPriceId("basic"),
        //  REQUIRED - Name of the plan, displayed on the pricing page
        name: "Basic",
        // A friendly description of the plan, displayed on the pricing page. Tip: explain why this plan and not others
        description: "Get inspired with one motivational quote daily",
        // The price you want to display, the one user will be charged on Stripe.
        price: 2.99,
        // If you have an anchor price (i.e. $29) that you want to display crossed out, put it here. Otherwise, leave it empty
        priceAnchor: 4.99,
        annualPrice: 23.88,
        annualPriceAnchor: 4.99 * 12,
        features: [
          {
            name: "1 quote daily",
          },
          { name: "Email or Text" },
          { name: "Choose custom time" },
        ],
      },
      {
        priceId: getPriceId("premium"),
        annualPriceId: getAnnualPriceId("premium"),
        // This plan will look different on the pricing page, it will be highlighted. You can only have one plan with isFeatured: true
        isFeatured: true,
        name: "Premium",
        description: "Stay uplifted with up to five quotes daily",
        price: 4.99,
        priceAnchor: 9.99,
        annualPrice: 39.99,
        annualPriceAnchor: 9.99 * 12,
        features: [
          {
            name: "5 quotes daily",
          },
          { name: "Email or Text" },
          { name: "Choose custom time" },
        ],
      },
    ],
  },
  aws: {
    // If you use AWS S3/Cloudfront, put values in here
    bucket: "bucket-name",
    bucketUrl: `https://bucket-name.s3.amazonaws.com/`,
    cdn: "https://cdn-id.cloudfront.net/",
    region: "us-east-1",
  },
  ses: {
    subdomain: "mail",
    fromNoReply: `QuotePulse <noreply@mail.${URL_BASE}>`,
    fromAdmin: `QuotePulse <admin@mail.${URL_BASE}>`,
    supportEmail: `admin@mail.${URL_BASE}`,
    forwardRepliesTo: "n3s.online@gmail.com",
  },
  mailgun: {
    // subdomain to use when sending emails, if you don't have a subdomain, just remove it. Highly recommended to have one (i.e. mg.yourdomain.com or mail.yourdomain.com)
    subdomain: "mg",
    // REQUIRED — Email 'From' field to be used when sending magic login links
    fromNoReply: `QuotePulse <noreply@mail.${URL_BASE}>`,
    // REQUIRED — Email 'From' field to be used when sending other emails, like abandoned carts, updates etc..
    fromAdmin: `QuotePulse <noreply@mail.${URL_BASE}>`,
    // Email shown to customer if need support. Leave empty if not needed => if empty, set up Crisp above, otherwise you won't be able to offer customer support."
    supportEmail: `admin@mail.${URL_BASE}`,
    // When someone replies to supportEmail sent by the app, forward it to the email below (otherwise it's lost). If you set supportEmail to empty, this will be ignored.
    forwardRepliesTo: "n3s.online@gmail.com",
  },
  colors: {
    // REQUIRED — The DaisyUI theme to use (added to the main layout.js). Leave blank for default (light & dark mode). If you any other theme than light/dark, you need to add it in config.tailwind.js in daisyui.themes.
    theme: "light",
    // REQUIRED — This color will be reflected on the whole app outside of the document (loading bar, Chrome tabs, etc..). By default it takes the primary color from your DaisyUI theme (make sure to update your the theme name after "data-theme=")
    // OR you can just do this to use a custom color: main: "#f37055". HEX only.
    main: themes["light"]["primary"],
  },
  auth: {
    // REQUIRED — the path to log in users. It's use to protect private routes (like /dashboard). It's used in apiClient (/libs/api.js) upon 401 errors from our API
    loginUrl: "/api/auth/signin",
    // REQUIRED — the path you want to redirect users after successfull login (i.e. /dashboard, /private). This is normally a private page for users to manage their accounts. It's used in apiClient (/libs/api.js) upon 401 errors from our API & in ButtonSignin.js
    callbackUrl: "/dashboard",
  },
} as ConfigProps;

export default config;
